import { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import { toast } from 'react-hot-toast'
import { logger } from '@/utils/logger'

interface Snapshot {
  id: string
  name: string
  description: string
  image_url: string
  thumbnail_url?: string
  zoom_level: number
  viewport_x: number
  viewport_y: number
  viewport_width: number
  viewport_height: number
  created_at: string
}

interface SnapshotManagerPanelProps {
  slideId: string
  viewer: any
  onClose?: () => void
  onStartRegionSelect?: () => void
  onEndRegionSelect?: () => void
}

export const SnapshotManagerPanel = forwardRef<any, SnapshotManagerPanelProps>(({
  slideId,
  viewer,
  onClose,
  onStartRegionSelect,
  onEndRegionSelect
}, ref) => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isSelectingRegion, setIsSelectingRegion] = useState(false)
  const [showCaptureDialog, setShowCaptureDialog] = useState(false)
  const [captureData, setCaptureData] = useState<any>(null)
  const [captureName, setCaptureName] = useState('')
  const [captureDescription, setCaptureDescription] = useState('')
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | null>(null)

  // Debug props
  useEffect(() => {
    console.log('🔵 SnapshotManagerPanel mounted/updated. viewer:', !!viewer, 'isCapturing:', isCapturing)
  }, [viewer, isCapturing])

  // Load snapshots
  const loadSnapshots = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/v1/pathology/snapshots/?slide_id=${slideId}`)
      if (response.ok) {
        const data = await response.json()
        setSnapshots(data.results || data)
        logger.debug('📸 Loaded snapshots:', data)
      } else {
        console.error('Failed to load snapshots:', response.status)
      }
    } catch (error) {
      console.error('Error loading snapshots:', error)
      toast.error('Không thể tải danh sách ảnh')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (slideId) {
      loadSnapshots()
    }
  }, [slideId])

  // Expose handleCapture to parent
  useImperativeHandle(ref, () => ({
    handleCapture: (customBounds?: any) => handleCapture(customBounds)
  }))

  // Start region selection mode
  const handleStartRegionSelect = () => {
    console.log('🎯 handleStartRegionSelect called')
    setIsSelectingRegion(true)
    console.log('🎯 isSelectingRegion set to true')
    if (onStartRegionSelect) {
      console.log('🎯 Calling onStartRegionSelect callback')
      onStartRegionSelect()
    } else {
      console.log('❌ onStartRegionSelect callback not provided')
    }
    toast.success('✏️ Vẽ hình chữ nhật để chọn vùng chụp')
  }

  // Cancel region selection
  const handleCancelRegionSelect = () => {
    setIsSelectingRegion(false)
    if (onEndRegionSelect) {
      onEndRegionSelect()
    }
  }

  // Capture snapshot (with or without region)
  const handleCapture = async (customBounds?: any) => {
    if (!viewer) {
      toast.error('Viewer chưa sẵn sàng')
      return
    }

    setIsCapturing(true)

    try {
      // Get viewport info
      const viewport = viewer.viewport
      const zoom = viewport.getZoom()

      let bounds, center
      if (customBounds) {
        // Use custom region bounds
        bounds = customBounds
        center = {
          x: bounds.x + bounds.width / 2,
          y: bounds.y + bounds.height / 2
        }
      } else {
        // Use current viewport
        bounds = viewport.getBounds()
        center = viewport.getCenter()
      }

      // Get canvas
      const canvas = viewer.drawer.canvas as HTMLCanvasElement
      if (!canvas) {
        throw new Error('Không tìm thấy canvas')
      }

      let imageData
      if (customBounds) {
        // Crop canvas to region
        const viewportRect = viewer.viewport.viewportToViewerElementRectangle(new (window as any).OpenSeadragon.Rect(
          customBounds.x,
          customBounds.y,
          customBounds.width,
          customBounds.height
        ))

        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = Math.abs(viewportRect.width)
        tempCanvas.height = Math.abs(viewportRect.height)
        const ctx = tempCanvas.getContext('2d')

        if (ctx) {
          ctx.drawImage(
            canvas,
            viewportRect.x, viewportRect.y, viewportRect.width, viewportRect.height,
            0, 0, tempCanvas.width, tempCanvas.height
          )
          imageData = tempCanvas.toDataURL('image/jpeg', 0.95)
        } else {
          imageData = canvas.toDataURL('image/jpeg', 0.95)
        }
      } else {
        // Full viewport
        imageData = canvas.toDataURL('image/jpeg', 0.95)
      }

      // Store capture data
      setCaptureData({
        imageData,
        viewport: {
          x: center.x,
          y: center.y,
          zoom: zoom,
          width: bounds.width,
          height: bounds.height
        }
      })

      setShowCaptureDialog(true)
      setIsSelectingRegion(false)
      if (onEndRegionSelect) {
        onEndRegionSelect()
      }
      toast.success('📸 Đã chụp ảnh! Hãy nhập thông tin')
    } catch (error) {
      console.error('Capture error:', error)
      toast.error('Không thể chụp ảnh')
    } finally {
      setIsCapturing(false)
    }
  }

  // Save snapshot
  const handleSave = async () => {
    if (!captureData) return

    try {
      // Convert base64 to blob
      const response = await fetch(captureData.imageData)
      const blob = await response.blob()

      // Create FormData
      const formData = new FormData()
      formData.append('slide_id', slideId)
      formData.append('name', captureName || `Snapshot ${new Date().toLocaleString('vi-VN')}`)
      formData.append('description', captureDescription)
      formData.append('viewport_x', captureData.viewport.x.toString())
      formData.append('viewport_y', captureData.viewport.y.toString())
      formData.append('zoom_level', captureData.viewport.zoom.toString())
      formData.append('viewport_width', captureData.viewport.width.toString())
      formData.append('viewport_height', captureData.viewport.height.toString())
      formData.append('image', blob, `snapshot_${Date.now()}.jpg`)

      // Upload
      const uploadResponse = await fetch('/api/v1/pathology/snapshots/', {
        method: 'POST',
        body: formData
      })

      if (!uploadResponse.ok) {
        throw new Error('Upload failed')
      }

      await uploadResponse.json()

      toast.success('✅ Đã lưu ảnh!')
      setShowCaptureDialog(false)
      setCaptureName('')
      setCaptureDescription('')
      setCaptureData(null)

      // Reload snapshots
      loadSnapshots()
    } catch (error) {
      console.error('Save error:', error)
      toast.error('Không thể lưu ảnh')
    }
  }

  // Delete snapshot
  const handleDelete = async (snapshotId: string) => {
    if (!confirm('Bạn có chắc muốn xóa ảnh này?')) {
      return
    }

    try {
      const response = await fetch(`/api/v1/pathology/snapshots/${snapshotId}/`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Đã xóa ảnh')
        loadSnapshots()
        if (selectedSnapshot?.id === snapshotId) {
          setSelectedSnapshot(null)
        }
      } else {
        throw new Error('Delete failed')
      }
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Không thể xóa ảnh')
    }
  }

  // Navigate to snapshot location
  const handleViewSnapshot = (snapshot: Snapshot) => {
    if (!viewer) return

    const point = new (window as any).OpenSeadragon.Point(
      snapshot.viewport_x,
      snapshot.viewport_y
    )

    viewer.viewport.panTo(point, true)
    viewer.viewport.zoomTo(snapshot.zoom_level, null, true)

    setSelectedSnapshot(snapshot)
    toast.success(`Đã di chuyển đến vị trí snapshot`)
  }

  return (
    <div className="w-96 bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          </svg>
          <h3 className="font-bold text-lg">Quản lý Snapshot</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* Capture Buttons */}
      <div className="p-4 bg-gray-50 border-b">
        {isSelectingRegion ? (
          <div className="space-y-2">
            <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-3 text-center">
              <p className="text-blue-800 font-medium text-sm mb-1">🖱️ Chế độ chọn vùng</p>
              <p className="text-blue-600 text-xs">Click và kéo trên ảnh để vẽ vùng chụp</p>
            </div>
            <button
              onClick={handleCancelRegionSelect}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              ✕ Hủy chọn vùng
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => handleCapture()}
              disabled={isCapturing || !viewer}
              className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center gap-2 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                </svg>
                <span>{isCapturing ? 'Đang chụp...' : '📸 Chụp toàn bộ màn hình'}</span>
            </button>
            <button
              onClick={() => {
                console.log('🔴 Button clicked! isCapturing:', isCapturing, 'viewer:', !!viewer)
                handleStartRegionSelect()
              }}
              disabled={isCapturing || !viewer}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              <span>✏️ Chọn vùng để chụp</span>
            </button>
          </div>
        )}
      </div>

      {/* Snapshots List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Đang tải...</div>
        ) : snapshots.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-16 h-16 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            </svg>
            <p>Chưa có ảnh nào</p>
            <p className="text-sm mt-1">Bấm nút "Chụp ảnh" để tạo snapshot</p>
          </div>
        ) : (
          <div className="space-y-3">
            {snapshots.map((snapshot) => (
              <div
                key={snapshot.id}
                className={`border-2 rounded-lg overflow-hidden hover:shadow-lg transition-all ${
                  selectedSnapshot?.id === snapshot.id ? 'border-purple-500 shadow-lg' : 'border-gray-200'
                }`}
              >
                {/* Thumbnail */}
                <div className="relative bg-gray-100">
                  <img
                    src={snapshot.thumbnail_url || snapshot.image_url}
                    alt={snapshot.name}
                    className="w-full h-32 object-cover cursor-pointer"
                    onClick={() => handleViewSnapshot(snapshot)}
                  />
                  <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {snapshot.zoom_level.toFixed(1)}X
                  </div>
                </div>

                {/* Info */}
                <div className="p-3 bg-white">
                  <h4 className="font-medium text-sm text-gray-900 mb-1 truncate">
                    {snapshot.name || `Snapshot ${snapshot.id.slice(0, 8)}`}
                  </h4>
                  {snapshot.description && (
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{snapshot.description}</p>
                  )}
                  <div className="text-xs text-gray-500 mb-2">
                    📍 ({Math.round(snapshot.viewport_x)}, {Math.round(snapshot.viewport_y)})
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewSnapshot(snapshot)}
                      className="flex-1 bg-blue-500 text-white text-xs py-2 px-3 rounded hover:bg-blue-600 transition-colors flex items-center justify-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Xem
                    </button>
                    <button
                      onClick={() => handleDelete(snapshot.id)}
                      className="flex-1 bg-red-500 text-white text-xs py-2 px-3 rounded hover:bg-red-600 transition-colors flex items-center justify-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Xóa
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Capture Dialog */}
      {showCaptureDialog && captureData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">💾 Lưu ảnh chụp</h2>

              {/* Preview */}
              <div className="mb-4 border rounded-lg overflow-hidden">
                <img
                  src={captureData.imageData}
                  alt="Snapshot preview"
                  className="w-full h-auto"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                />
              </div>

              {/* Metadata */}
              <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
                <p><strong>Zoom:</strong> {captureData.viewport.zoom.toFixed(2)}x</p>
                <p><strong>Vị trí:</strong> ({captureData.viewport.x.toFixed(0)}, {captureData.viewport.y.toFixed(0)})</p>
                <p><strong>Kích thước:</strong> {captureData.viewport.width.toFixed(0)} × {captureData.viewport.height.toFixed(0)}</p>
              </div>

              {/* Name Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Tên ảnh:</label>
                <input
                  type="text"
                  value={captureName}
                  onChange={(e) => setCaptureName(e.target.value)}
                  placeholder="Ví dụ: Tế bào bất thường vùng trung tâm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Description Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Mô tả (tùy chọn):</label>
                <textarea
                  value={captureDescription}
                  onChange={(e) => setCaptureDescription(e.target.value)}
                  placeholder="Ghi chú thêm về ảnh này..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 font-medium transition-colors"
                >
                  💾 Lưu ảnh
                </button>
                <button
                  onClick={() => {
                    setShowCaptureDialog(false)
                    setCaptureData(null)
                    setCaptureName('')
                    setCaptureDescription('')
                  }}
                  className="px-6 py-2 border-2 border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

SnapshotManagerPanel.displayName = 'SnapshotManagerPanel'

export default SnapshotManagerPanel
